import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';

const GlobalErrorContext = createContext();

export const useGlobalError = () => useContext(GlobalErrorContext);

const GENERIC_ERROR_MESSAGE = "An unexpected error occurred. Please try again or contact support.";

export const GlobalErrorProvider = ({ children }) => {
    const [notification, setNotification] = useState(null);

    // Generic notification handler
    const showNotification = useCallback((message, severity = 'error', action = null) => {
        setNotification({ message, severity, action });
    }, []);

    // Helper for errors (backward compatibility + convenience)
    const showError = useCallback((message) => {
        showNotification(message, 'error');
    }, [showNotification]);

    // Helper for success messages
    const showSuccess = useCallback((message, action = null) => {
        showNotification(message, 'success', action);
    }, [showNotification]);

    const clearNotification = useCallback(() => {
        setNotification(null);
    }, []);

    useEffect(() => {
        const handleUnhandledRejection = (event) => {
            // Log technical details to console for developers
            console.error("Unhandled Promise Rejection:", event.reason);
            
            // Show generic message to user
            showError(GENERIC_ERROR_MESSAGE);
        };

        const handleWindowError = (event) => {
            // Log technical details to console for developers
            console.error("Global Script Error:", event.error || event.message);
            
            // Show generic message to user
            showError(GENERIC_ERROR_MESSAGE);
        };

        window.addEventListener('unhandledrejection', handleUnhandledRejection);
        window.addEventListener('error', handleWindowError);

        return () => {
            window.removeEventListener('unhandledrejection', handleUnhandledRejection);
            window.removeEventListener('error', handleWindowError);
        };
    }, [showError]);

    return (
        <GlobalErrorContext.Provider value={{ 
            notification, 
            showError, 
            showSuccess, 
            showNotification,
            clearNotification, 
            GENERIC_ERROR_MESSAGE 
        }}>
            {children}
        </GlobalErrorContext.Provider>
    );
};
